import React, { useState, useEffect, useRef } from 'react';
import { Clock, Play, Pause, RotateCcw, Bell, Volume2, VolumeX } from 'lucide-react';
import { SEO } from '../components/SEO';

function OnlineOdbrojavanje() {
  const [hours, setHours] = useState<number>(0);
  const [minutes, setMinutes] = useState<number>(0);
  const [seconds, setSeconds] = useState<number>(0);
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(true);
  const [isFinished, setIsFinished] = useState<boolean>(false);
  const [audioError, setAudioError] = useState<boolean>(false);
  
  const intervalRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    // Using multiple audio sources for better browser compatibility
    audioRef.current = new Audio();
    audioRef.current.src = 'https://assets.mixkit.co/active_storage/sfx/989/989-preview.mp3';
    audioRef.current.loop = true;

    // Add error handling for audio loading
    audioRef.current.addEventListener('error', () => {
      console.error('Error loading audio file');
      setAudioError(true);
      setIsSoundEnabled(false);
    });
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (isRunning) {
      intervalRef.current = window.setInterval(() => {
        setTotalSeconds(prevSeconds => {
          if (prevSeconds <= 1) {
            clearInterval(intervalRef.current!);
            setIsRunning(false);
            setIsFinished(true);
            if (isSoundEnabled && audioRef.current && !audioError) {
              audioRef.current.play().catch(e => {
                console.error("Error playing sound:", e);
                setAudioError(true);
                setIsSoundEnabled(false);
              });
            }
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);
    } else if (intervalRef.current) {
      window.clearInterval(intervalRef.current);
    }
    
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, isSoundEnabled, audioError]);

  useEffect(() => {
    // Update hours, minutes, seconds when totalSeconds changes
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    setHours(h);
    setMinutes(m);
    setSeconds(s);
  }, [totalSeconds]);

  const handleStartStop = () => {
    if (isFinished) {
      stopAlarm();
    }
    
    if (totalSeconds > 0) {
      setIsRunning(!isRunning);
    }
  };

  const handleReset = () => {
    setIsRunning(false);
    stopAlarm();
    setTotalSeconds(hours * 3600 + minutes * 60 + seconds);
    setIsFinished(false);
  };

  const handleSetTime = () => {
    const total = hours * 3600 + minutes * 60 + seconds;
    setTotalSeconds(total);
    setIsFinished(false);
  };

  const stopAlarm = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsFinished(false);
  };

  const toggleSound = () => {
    if (audioError) {
      return; // Don't allow enabling sound if there was an error
    }
    setIsSoundEnabled(!isSoundEnabled);
    if (isFinished && audioRef.current) {
      if (isSoundEnabled) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => {
          console.error("Error playing sound:", e);
          setAudioError(true);
          setIsSoundEnabled(false);
        });
      }
    }
  };

  const formatTime = (time: number): string => {
    return time.toString().padStart(2, '0');
  };

  // Predefined time presets
  const timePresets = [
    { name: '1 minuta', seconds: 60 },
    { name: '3 minute', seconds: 180 },
    { name: '5 minuta', seconds: 300 },
    { name: '10 minuta', seconds: 600 },
    { name: '15 minuta', seconds: 900 },
    { name: '30 minuta', seconds: 1800 },
    { name: '1 sat', seconds: 3600 },
  ];

  const setPresetTime = (seconds: number) => {
    setIsRunning(false);
    stopAlarm();
    setTotalSeconds(seconds);
    setIsFinished(false);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Online Odbrojavanje",
    "applicationCategory": "UtilityApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Besplatni online timer za odbrojavanje s alarmom. Postavite vrijeme i pratite odbrojavanje do nule s zvučnim upozorenjem.",
    "url": "https://online-kalkulator.com/online-stoperica/odbrojavanje",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Precizno odbrojavanje vremena",
      "Zvučni alarm",
      "Unaprijed postavljeni vremenski intervali",
      "Jednostavno sučelje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Online Odbrojavanje | Besplatni Timer s Alarmom"
        description="Besplatni online timer za odbrojavanje s alarmom. Postavite vrijeme i pratite odbrojavanje do nule s zvučnim upozorenjem."
        canonicalUrl="/online-stoperica/odbrojavanje"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Clock className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Online Odbrojavanje</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Timer Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Time Display */}
              <div className="text-center">
                <div className={`text-6xl font-mono font-bold tracking-wider ${isFinished ? 'text-red-600 animate-pulse' : 'text-blue-600'}`}>
                  {formatTime(hours)}:{formatTime(minutes)}:{formatTime(seconds)}
                </div>
              </div>

              {/* Time Input */}
              {!isRunning && !isFinished && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sati
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="23"
                      value={hours}
                      onChange={(e) => setHours(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minute
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={minutes}
                      onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Sekunde
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={seconds}
                      onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}

              {/* Presets */}
              {!isRunning && !isFinished && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Brzi odabir
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {timePresets.map((preset) => (
                      <button
                        key={preset.seconds}
                        onClick={() => setPresetTime(preset.seconds)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm transition"
                      >
                        {preset.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Controls */}
              <div className="flex justify-center gap-4">
                {!isRunning && !isFinished && totalSeconds === 0 ? (
                  <button
                    onClick={handleSetTime}
                    className="flex items-center justify-center px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                    disabled={hours === 0 && minutes === 0 && seconds === 0}
                  >
                    Postavi vrijeme
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleStartStop}
                      className={`flex items-center justify-center w-14 h-14 rounded-full ${
                        isRunning ? 'bg-red-500 hover:bg-red-600' : 'bg-green-500 hover:bg-green-600'
                      } text-white transition-colors`}
                      aria-label={isRunning ? 'Pause' : 'Start'}
                    >
                      {isRunning ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                    </button>
                    
                    <button
                      onClick={handleReset}
                      className="flex items-center justify-center w-14 h-14 rounded-full bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                      aria-label="Reset"
                    >
                      <RotateCcw className="w-6 h-6" />
                    </button>
                    
                    <button
                      onClick={toggleSound}
                      className={`flex items-center justify-center w-14 h-14 rounded-full ${
                        audioError ? 'bg-gray-300 cursor-not-allowed' :
                        isSoundEnabled ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-400 hover:bg-gray-500'
                      } text-white transition-colors`}
                      aria-label={isSoundEnabled ? 'Mute' : 'Unmute'}
                      disabled={audioError}
                    >
                      {isSoundEnabled ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
                    </button>
                  </>
                )}
              </div>

              {/* Status Messages */}
              {audioError && (
                <div className="text-center py-3 px-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-600">
                    Zvuk nije dostupan. Provjerite postavke zvuka vašeg uređaja.
                  </p>
                </div>
              )}
              
              {isFinished && (
                <div className="text-center py-3 px-4 bg-red-50 rounded-lg">
                  <div className="flex items-center justify-center gap-2">
                    <Bell className="w-5 h-5 text-red-500" />
                    <p className="text-lg font-medium text-red-600">Vrijeme je isteklo!</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Online Odbrojavanju</h2>
                <p className="text-gray-600 leading-relaxed">
                  Online odbrojavanje je koristan alat koji vam omogućuje postavljanje timera koji
                  odbrojava od zadanog vremena do nule. Kada odbrojavanje završi, oglasit će se zvučni
                  alarm koji vas obavještava da je vrijeme isteklo. Idealan je za kuhanje, vježbanje,
                  učenje ili bilo koju drugu aktivnost koja zahtijeva vremensko ograničenje.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Značajke</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Precizno odbrojavanje vremena u satima, minutama i sekundama</li>
                  <li>Zvučni alarm kada vrijeme istekne</li>
                  <li>Mogućnost uključivanja i isključivanja zvuka</li>
                  <li>Brzi odabir unaprijed postavljenih vremenskih intervala</li>
                  <li>Jednostavne kontrole za pokretanje, pauziranje i resetiranje</li>
                  <li>Radi na svim uređajima (računalo, tablet, mobitel)</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Postavite željeno vrijeme odbrojavanja:
                    <ul className="list-none pl-6 pt-2 space-y-1">
                      <li>- Unesite sate, minute i sekunde u odgovarajuća polja, ili</li>
                      <li>- Odaberite jedan od unaprijed postavljenih vremenskih intervala</li>
                    </ul>
                  </li>
                  <li>Kliknite na "Postavi vrijeme" za potvrdu</li>
                  <li>Kliknite na gumb <span className="text-green-600">▶</span> za pokretanje odbrojavanja</li>
                  <li>Kliknite na gumb <span className="text-red-600">❚❚</span> za pauziranje odbrojavanja</li>
                  <li>Kliknite na gumb <span className="text-gray-600">↺</span> za resetiranje odbrojavanja</li>
                  <li>Kliknite na gumb <span className="text-blue-600">🔊</span>/<span className="text-gray-600">🔇</span> za uključivanje/isključivanje zvuka</li>
                  <li>Kada vrijeme istekne, oglasit će se zvučni alarm</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjene</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Kuhanje i pečenje</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Praćenje vremena kuhanja</li>
                      <li>Mjerenje vremena pečenja</li>
                      <li>Kontrola vremena za kuhanje jaja</li>
                      <li>Praćenje vremena za dizanje tijesta</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Vježbanje i sport</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Intervalni treninzi</li>
                      <li>Odmori između setova</li>
                      <li>Planiranje treninga</li>
                      <li>Istezanje i zagrijavanje</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Učenje i produktivnost</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Pomodoro tehnika (25 min rada, 5 min odmora)</li>
                      <li>Vremensko ograničenje za zadatke</li>
                      <li>Kontrola vremena za prezentacije</li>
                      <li>Pauze tijekom rada</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-700 mb-2">Svakodnevna upotreba</h3>
                    <ul className="list-disc list-inside text-gray-600 space-y-1">
                      <li>Ograničenje vremena za igru</li>
                      <li>Podsjetnik za uzimanje lijekova</li>
                      <li>Praćenje vremena parkiranja</li>
                      <li>Kontrola vremena za pranje zubi</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Savjeti za Korištenje</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Koristite brzi odabir za često korištene vremenske intervale</li>
                  <li>Provjerite je li zvuk na vašem uređaju uključen ako želite čuti alarm</li>
                  <li>Odbrojavanje će nastaviti raditi čak i ako prebacite na drugu karticu ili aplikaciju</li>
                  <li>Za preciznije mjerenje, izbjegavajte prebacivanje između aplikacija na mobilnim uređajima</li>
                  <li>Postavite više timera istovremeno otvaranjem više kartica</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Tehničke Informacije</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Odbrojavanje radi u pregledniku i ne zahtijeva instalaciju</li>
                  <li>Zvučni alarm zahtijeva da vaš uređaj ima zvučnike i da nije utišan</li>
                  <li>Podaci se ne spremaju nakon zatvaranja stranice</li>
                  <li>Odbrojavanje ne koristi internetsku vezu nakon učitavanja stranice</li>
                  <li>Kompatibilno je sa svim modernim web preglednicima</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OnlineOdbrojavanje;